import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow, coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaRegClone } from 'react-icons/fa6';
import { LuCircleCheck } from 'react-icons/lu';

// Separate component for code blocks with copy functionality
const CodeBlock = ({ language, children, darkMode }) => {
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const codeTimeoutRef = useRef(null);

  const codeContent = String(children).replace(/\n$/, '');

  const handleCopyCode = () => {
    // Clear any existing timeout
    if (codeTimeoutRef.current) {
      clearTimeout(codeTimeoutRef.current);
    }

    navigator.clipboard.writeText(codeContent);

    // Set copied state to true
    setIsCodeCopied(true);

    // Reset after 3 seconds
    codeTimeoutRef.current = setTimeout(() => {
      setIsCodeCopied(false);
    }, 3000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (codeTimeoutRef.current) {
        clearTimeout(codeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={(e) => {
        if (!isCodeCopied) {
          e.currentTarget.querySelector('button').style.opacity = '1';
        }
      }}
      onMouseLeave={(e) => {
        if (!isCodeCopied) {
          e.currentTarget.querySelector('button').style.opacity = '0';
        }
      }}
    >
      <SyntaxHighlighter
        style={darkMode ? tomorrow : coy}
        language={language}
        PreTag="div"
        customStyle={{
          margin: '0',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontFamily:
            'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          background: darkMode ? '#161616' : '#f6f8fa',
          backgroundColor: darkMode ? '#161616' : '#f6f8fa',
          border: darkMode ? '1px solid #282828' : '1px solid #e1e4e8',
          maxWidth: '100%',
          minWidth: 0,
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
        codeTagProps={{
          style: {
            background: 'transparent !important',
            backgroundColor: 'transparent !important',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          },
        }}
      >
        {codeContent}
      </SyntaxHighlighter>
      <button
        onClick={handleCopyCode}
        className="absolute top-2 right-2 p-2 rounded-md opacity-0 transition-opacity duration-200 hover:bg-[#e0e0e0] dark:hover:bg-[#222222] text-black dark:text-white"
        title="Copy code"
        style={{ opacity: isCodeCopied ? '1' : '0' }}
      >
        <div className="relative w-4 h-4">
          <div
            className={`absolute inset-0 transition-all duration-200 ease-in-out ${
              isCodeCopied ? 'opacity-0 scale-75' : 'opacity-100 scale-100'
            }`}
          >
            <FaRegClone className="h-4 w-4" />
          </div>
          <div
            className={`absolute inset-0 transition-all duration-200 ease-in-out ${
              isCodeCopied ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
            }`}
          >
            <LuCircleCheck className="h-4 w-4 text-black dark:text-white" />
          </div>
        </div>
      </button>
    </div>
  );
};

export default CodeBlock;
